"use client";

import React from "react";
import Link from "next/link";
import { Users } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { SmartUserDisplay } from "@/components/shared/smart-display/SmartUserDisplay";
import type { AssigneeAggregate, AppUserWithRoles } from "./types";
import { formatStatusLabel, formatTaskDate } from "./utils";

interface TicketAssigneeCardProps {
  aggregate: AssigneeAggregate;
}

/**
 * Profile card for a single assignee: directory details + list of the ticket's
 * tasks owned by that user. When the user is missing from the directory we
 * show only an "unknown profile" hint so the row still renders.
 */
export function TicketAssigneeCard({ aggregate }: TicketAssigneeCardProps) {
  const { userId, user, tasks: assignedTasks } = aggregate;
  const userWithRoles = user;
  const roleLabel =
    userWithRoles?.roles?.display_name ||
    userWithRoles?.roles?.name ||
    user?.role ||
    "—";

  return (
    <Card key={userId} className="overflow-hidden">
      <CardHeader className="pb-2 border-b border-gray-100 bg-card-50">
        <CardTitle className="text-base flex flex-wrap items-center gap-3">
          <Users className="h-4 w-4 text-gray-500 shrink-0" />
          <SmartUserDisplay
            userId={userId}
            userData={user || undefined}
            fallbackText={userId}
            showIcon={false}
          />
          {user?.is_active === false ? (
            <Badge variant="secondary" className="text-xs">
              Inactive
            </Badge>
          ) : null}
        </CardTitle>
      </CardHeader>
      <CardContent className="pt-4 space-y-4">
        {user ? (
          <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2 text-sm">
            <div>
              <dt className="text-gray-500">Email</dt>
              <dd className="text-card-foreground font-medium break-all">{user.email}</dd>
            </div>
            <div>
              <dt className="text-gray-500">Name</dt>
              <dd className="text-card-foreground">
                {[user.first_name, user.last_name].filter(Boolean).join(" ") || "—"}
              </dd>
            </div>
            <div>
              <dt className="text-gray-500">Role</dt>
              <dd className="text-card-foreground">{roleLabel}</dd>
            </div>
            <div>
              <dt className="text-gray-500">Account status</dt>
              <dd className="text-card-foreground">
                {user.is_active ? "Active" : "Inactive"}
              </dd>
            </div>
            <div>
              <dt className="text-gray-500">Member since</dt>
              <dd className="text-card-foreground">{formatTaskDate(user.created_at)}</dd>
            </div>
            <div>
              <dt className="text-gray-500">User ID</dt>
              <dd className="text-card-foreground font-mono text-xs break-all">
                {user.id}
              </dd>
            </div>
          </dl>
        ) : (
          <p className="text-sm text-amber-700">
            Profile not found in directory (id:{" "}
            <span className="font-mono text-xs">{userId}</span>). They may be a client user
            or missing from admin user list.
          </p>
        )}

        <div>
          <h4 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-2">
            Tasks on this ticket ({assignedTasks.length})
          </h4>
          <ul className="space-y-1.5">
            {assignedTasks.map((t) => (
              <li key={t.id} className="flex flex-wrap items-center gap-2 text-sm">
                <Link
                  href={`/tasks/${t.id}`}
                  className="text-blue-600 hover:underline font-medium"
                >
                  {t.title}
                </Link>
                {t.status ? (
                  <Badge
                    variant="outline"
                    className="text-xs capitalize font-normal"
                  >
                    {formatStatusLabel(t.status)}
                  </Badge>
                ) : null}
              </li>
            ))}
          </ul>
        </div>
      </CardContent>
    </Card>
  );
}
